import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Edge, Node as SchemaNode } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { reconcileGraphNodes } from '../../helpers/graph/data-processing';
import { areGraphsEqual } from '../../helpers/graph/graph-utils';
import { isTerminalNode, isPhoneTreeDescendant } from '../../helpers/graph/node-utils';
import { getNodePlaceholderWarning, getNodeWarning } from '../../helpers/graph/warning-validators';
import { useCallRouteGraphsStore } from '../call-route-store';

export interface UseCallRouteGraphStateParams {
  callRouteId: string;
  callRoute: CallRouteV1.Types.ReadIO['output'] | undefined;
  tenantId: string;
  context?: {
    contextNodeId?: string;
    contextPrimitiveId?: string;
  };
  onShouldForceReset: () => void;
  isCallObjectsDataLoading: boolean;
}

/**
 * Hook to manage call route graph state (nodes, edges, editing state).
 * Handles syncing with the store, temporary editing state, and context.
 */
export const useCallRouteGraphState = ({
  callRouteId,
  callRoute,
  tenantId,
  context,
  onShouldForceReset,
  isCallObjectsDataLoading,
}: UseCallRouteGraphStateParams) => {
  const {
    allNodes,
    allEdges,
    allTempEditingNodes,
    allTempEditingEdges,
    setNodesByCallRouteId,
    setEdgesByCallRouteId,
    setTempEditingNodesByCallRouteId,
    setTempEditingEdgesByCallRouteId,
    getNodesByCallRouteId,
    getEdgesByCallRouteId,
    getTempEditingNodesByCallRouteId,
    getTempEditingEdgesByCallRouteId,
  } = useCallRouteGraphsStore();

  const { mutateAsync: refreshNodes } = CallRouteV1.Mutations.useRefreshNodesMutation();

  // The id of the node that should be focused on in the call route flow.
  const [contextNodeId, setContextNodeId] = useState<string | undefined>(undefined);

  const _prevNodesAndEdges = useRef<Record<string, { nodes: CallRouteV1.Types.Node[]; edges: Edge[] }>>({});
  // Ref to track the last reconciled tempEditingNodes to prevent infinite loops
  // When we update tempEditingNodes via reconciliation, we store the new value here
  // so the next effect run can skip if it's the same reference
  const _lastReconciledTempEditingNodes = useRef<CallRouteV1.Types.Node[] | undefined>(undefined);

  // Memoized graph state
  // Original nodes/edges from store for this call route
  const callRouteNodes = useMemo(() => getNodesByCallRouteId(callRouteId), [callRouteId, allNodes]);
  const callRouteEdges = useMemo(() => getEdgesByCallRouteId(callRouteId), [callRouteId, allEdges]);
  // Temporary editing nodes/edges for this call route
  const tempEditingNodes = useMemo(
    () => getTempEditingNodesByCallRouteId(callRouteId),
    [callRouteId, allTempEditingNodes]
  );
  const tempEditingEdges = useMemo(
    () => getTempEditingEdgesByCallRouteId(callRouteId),
    [callRouteId, allTempEditingEdges]
  );

  // Helper functions to get current nodes/edges (editing if exist, else original)
  const getNodes = useCallback(() => {
    const currentTempEditingNodes = getTempEditingNodesByCallRouteId(callRouteId);
    if (currentTempEditingNodes) {
      return currentTempEditingNodes;
    }
    return getNodesByCallRouteId(callRouteId) ?? [];
  }, [callRouteId, getTempEditingNodesByCallRouteId, getNodesByCallRouteId]);

  const getEdges = useCallback(() => {
    const currentTempEditingEdges = getTempEditingEdgesByCallRouteId(callRouteId);
    if (currentTempEditingEdges) {
      return currentTempEditingEdges;
    }
    return getEdgesByCallRouteId(callRouteId) ?? [];
  }, [callRouteId, getTempEditingEdgesByCallRouteId, getEdgesByCallRouteId]);

  // The call route nodes with some extra data for the flow.
  const extendedNodes = useMemo(() => {
    if (!callRouteNodes || !callRouteEdges) {
      return [];
    }

    // Use the temporary nodes/edges if they exist, otherwise use the nodes/edges from the call route.
    const nodesToUse = getNodes();
    const edgesToUse = getEdges();
    return nodesToUse.map((node) => {
      return {
        ...node,
        // We need to check if the nodes are a descendent of a phone tree node, and if so, we add
        // an extra property to the node to indicate that it is a descendent of a phone tree.
        isPhoneTreeDescendent:
          node.type !== CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE &&
          isPhoneTreeDescendant(node.id, nodesToUse, edgesToUse),
        warning: getNodeWarning(node),
        isExtendable:
          !isTerminalNode(node) &&
          // We treat Weave Assistant nodes as non-extendable for the purposes of this check since we don't actually
          // extend from the Weave Assistant node but rather from it's fallback child node.
          node.type !== CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT,
        placeholderWarning: getNodePlaceholderWarning(node, nodesToUse, edgesToUse),
      };
    });
    // Only depend on the actual data, not the getter functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callRouteNodes, callRouteEdges, tempEditingNodes, tempEditingEdges]);

  // Effect: Sync context node ID from navigation context
  useEffect(() => {
    if (!!contextNodeId) {
      // If we already have a contextNodeId set, then do not override it.
      return;
    }

    // context.contextNodeId will be set when a user redirects back to the call route details page
    // from a call object settings page. A user can redirect back to the call route details page
    // from the breadcrumbs after they have been redirected to a call object page by clicking on a call
    // object links in one of the nodes in the call route canvas.
    const ctxNodeId = context?.contextNodeId;
    if (ctxNodeId) {
      // If contextNodeId is set, then just set this as the value to use for the node id to focus on in the call route flow.
      setContextNodeId(ctxNodeId);
    }
    // context.contextPrimitiveId can be set when navigating from another call object settings page. It is the
    // primitive id of a call object.
    // We need to check if contextPrimitiveId is set and corresponds to a call object id that is part of the call route flow.
    // If it is, then we will set the id of the node that corresponds to this call object.
    const ctxPrimitiveId = context?.contextPrimitiveId;
    if (ctxPrimitiveId) {
      const nodes = getNodes();
      const node = nodes.find((n) => n.callObject.primitiveId === ctxPrimitiveId);
      if (node) {
        setContextNodeId(node.id);
      }
    }
  }, [context?.contextNodeId, context?.contextPrimitiveId, contextNodeId, getNodes]);

  // Effect: Sync nodes/edges from API to store
  // Updates the nodes and edges in the store when the call route data from the API changes.
  useEffect(() => {
    if (!callRoute) return;
    const latestNodesFromApi = callRoute?.nodes ?? [];
    const latestEdgesFromApi = callRoute?.edges ?? [];

    // This typescript assertion is where we are telling typescript that the nodes from the API are
    // of type CallRouteV1.Types.Node[] which is really the same type except it correctly narrows
    // the node type and callObject combinations.
    setNodesByCallRouteId(callRouteId, latestNodesFromApi as CallRouteV1.Types.Node[]);
    setEdgesByCallRouteId(callRouteId, latestEdgesFromApi);
  }, [callRoute, callRouteId, setNodesByCallRouteId, setEdgesByCallRouteId]);

  // Effect: Handle graph changes and reconcile editing state
  // When the nodes and the edges in the store change, if there are temporary editing changes, then we need to check
  // if the nodes and edges from the API are STRUCTURALLY different from the nodes and edges in the store.
  // If they are different, then we need to check if the user has made any changes to the call route flow
  // and show a modal to inform the user that the unsaved changes need to be reset.
  // If they are not STRUCTURALLY different, then we need to update the temp editing nodes and edges with the latest
  // data from the API.
  useEffect(() => {
    // Skip if this effect was triggered by our own reconciliation update.
    // We check if tempEditingNodes is the same reference we stored after reconciliation.
    // Only skip when tempEditingNodes is defined - when it's undefined (no active edits),
    // we need to allow the effect to run so that _prevNodesAndEdges.current gets updated
    // with the latest API data (e.g., after a save operation).
    if (tempEditingNodes !== undefined && tempEditingNodes === _lastReconciledTempEditingNodes.current) {
      return;
    }

    if (isCallObjectsDataLoading) return;

    const latestNodes = callRouteNodes ?? [];
    const latestEdges = callRouteEdges ?? [];
    const prevNodes = _prevNodesAndEdges.current[callRouteId]?.nodes ?? [];
    const prevEdges = _prevNodesAndEdges.current[callRouteId]?.edges ?? [];

    // If there are no previous nodes and edges, then we will set the previous nodes and edges to the latest nodes and
    // edges from the API and return. This is the first time the call route data is being set in the store.
    if (!prevNodes.length && !prevEdges.length) {
      _prevNodesAndEdges.current = {
        ..._prevNodesAndEdges.current,
        [callRouteId]: { nodes: latestNodes, edges: latestEdges },
      };
      return;
    }

    // Check if there are temporary editing nodes and edges in the store. If there are, then we need to compare
    // the latest nodes and edges from the API with nodes and edges that we have in the store to see if they are
    // structurally equal.
    if (tempEditingEdges && tempEditingNodes) {
      const areEqual = areGraphsEqual(
        { nodes: latestNodes, edges: latestEdges },
        { nodes: prevNodes, edges: prevEdges }
      );

      if (!areEqual) {
        // If the nodes and edges from the API are not equal to the nodes and edges in the store (graph structure only),
        // then we need to reset the temp editing nodes and edges and update the store with the latest nodes and edges
        // from the API. We will additionally show a modal to inform the user that the unsaved changes will be lost.
        onShouldForceReset();
        setTempEditingNodesByCallRouteId(callRouteId, undefined);
        setTempEditingEdgesByCallRouteId(callRouteId, undefined);
        // Clear the reconciled ref since we're resetting
        _lastReconciledTempEditingNodes.current = undefined;
      } else {
        // If the nodes and edges from the API are equal to the nodes and edges in the store (graph structure only),
        // then we need to make sure we get the latest node expansion data for any nodes that are being edited.
        refreshNodes({
          tenantId,
          nodes: tempEditingNodes as SchemaNode[],
        }).then((data) => {
          // The latest callObject data for each node is returned in the data.nodes array.
          const refreshedNodes = data.nodes ?? [];

          // Reconcile any editing data with the latest data from the API.
          const reconciledNodes = reconcileGraphNodes({
            tempEditingNodes,
            latestNodes: refreshedNodes as CallRouteV1.Types.Node[],
            prevNodes,
          });

          // Store the reconciled nodes reference before updating
          // so the next effect run can detect this was our update
          _lastReconciledTempEditingNodes.current = reconciledNodes;
          setTempEditingNodesByCallRouteId(callRouteId, reconciledNodes);
        });
      }
    }

    _prevNodesAndEdges.current = {
      ..._prevNodesAndEdges.current,
      [callRouteId]: { nodes: latestNodes, edges: latestEdges },
    };
  }, [
    callRouteNodes,
    callRouteEdges,
    callRouteId,
    isCallObjectsDataLoading,
    tempEditingNodes,
    tempEditingEdges,
    tenantId,
    onShouldForceReset,
    setTempEditingNodesByCallRouteId,
    setTempEditingEdgesByCallRouteId,
    refreshNodes,
  ]);

  // Reset editing state
  const resetGraphState = useCallback(() => {
    setTempEditingNodesByCallRouteId(callRouteId, undefined);
    setTempEditingEdgesByCallRouteId(callRouteId, undefined);
    // Clear the reconciled ref to prevent stale comparisons in the reconciliation effect
    _lastReconciledTempEditingNodes.current = undefined;
  }, [callRouteId, setTempEditingNodesByCallRouteId, setTempEditingEdgesByCallRouteId]);

  // Update editing state
  const updateEditingState = useCallback(
    (nodes: CallRouteV1.Types.Node[], edges: Edge[]) => {
      setTempEditingNodesByCallRouteId(callRouteId, nodes);
      setTempEditingEdgesByCallRouteId(callRouteId, edges);
    },
    [callRouteId, setTempEditingNodesByCallRouteId, setTempEditingEdgesByCallRouteId]
  );

  return {
    // State
    callRouteNodes,
    callRouteEdges,
    tempEditingNodes,
    tempEditingEdges,
    extendedNodes,
    contextNodeId,
    setContextNodeId,

    // Helpers
    getNodes,
    getEdges,

    // Actions
    resetGraphState,
    updateEditingState,

    // Store methods (pass-through for convenience)
    setNodesByCallRouteId,
    setEdgesByCallRouteId,
    setTempEditingNodesByCallRouteId,
    setTempEditingEdgesByCallRouteId,
  };
};
